import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Button from "react-bootstrap/Button";
import ListHeaderBackButton from "components/ListHeaderBackButton";
import { getErrorMessage } from "services/UtilityService";
import { sendAuthenticatedAsyncRequest, sendAuthenticatedAsyncRequestWithFinallyCallback } from "services/AsyncRequestService";
import { showNotification } from "services/NotificationService";
import "../../../assets/css/switch.css";
import ServerSideReactTable from "commons/table/ServerSideReactTable";
import {
  UNPROCESSED_FAX_LIST_COLUMNS,
  UNPROCESSED_FAX_LIST_HIDDEN_COLUMNS,
  UNKNOWN,
  ALL_FAX_TAG,
  DEFAULT_UNPROCESSED_FAX_LIST_SORTING,
  DEFAULT_UNPROCESSED_FAX_LIST_FILTER,
  UFL_URL_FILTER_CONFIG,
} from "lookup/UnprocessedFaxListConstantsV2";
import { UNPROCESSED_FAX_PROGRAM_TYPE } from "lookup/UnprocessedFaxListConstantsV2";
import {
  Tip,
  QUERY_FILTER_TYPES,
} from "commons/utils";
import { useSyncListPaginationToUrl } from "commons/hooks/useSyncListPaginationToUrl";
import {
  renderLastModifiedDateCol,
  getFaxProgramType,
  getFaxTypeForRoute,
  getUrlFilterValues,
  getFilterValueFromUrl,
  buildSearchFromPagination,
  normalizeMultiSelectIdsFilterValue,
  buildPrimaryFilters,
  filterModification,
  sortedModification,
} from "./UnprocessedFaxList.helpers";

const DEFAULT_PAGE_SIZE = 15;
const UFL_PROGRAM_TYPE_LIST_PATH = "/unprocessed-fax/program-type-list";
const SESSION_KEY = "unprocessedFaxPaginationState";
const POLL_TIMEOUT_SECONDS = 45;
const UFL_LIST_POLL_HTTP_TIMEOUT_MS = (POLL_TIMEOUT_SECONDS + 25) * 1000;
const POLL_RETRY_JITTER_MIN_MS = 200;
const POLL_RETRY_JITTER_MAX_MS = 2000;

const nextPollDelayMs = () =>
  POLL_RETRY_JITTER_MIN_MS +
  Math.floor(
    Math.random() * (POLL_RETRY_JITTER_MAX_MS - POLL_RETRY_JITTER_MIN_MS + 1)
  );

const UnprocessedFaxList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [pagination, setPagination] = useState(() => {
    const shouldReset = location.state?.reset;
    if (shouldReset) {
      sessionStorage.removeItem(SESSION_KEY);
    }

    const saved = sessionStorage.getItem(SESSION_KEY);
    if (!shouldReset && saved) return JSON.parse(saved);

    return {
      page: 0,
      pageSize: DEFAULT_PAGE_SIZE,
      sorted: DEFAULT_UNPROCESSED_FAX_LIST_SORTING,
      filtered: [],
      resized: [],
      expanded: {},
    };
  });

  const [faxList, setFaxList] = useState([]);
  const [totalFaxes, setTotalFaxes] = useState("Loading ...");
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const [switchAdminList, setSwitchAdminList] = useState([]);
  const [faxTagList, setFaxTagList] = useState([]);
  const [programName, setProgramName] = useState("");
  const [programType, setProgramType] = useState("");
  const [listColumns, setListColumns] = useState([]);
  const [lastModifiedDate, setLastModifiedDate] = useState(null);

  // Refs to manage polling lifecycle
  const pollingActiveRef = useRef(false);
  const pollingTimeoutRef = useRef(null);
  // Ref to always have the latest lastModifiedDate value (avoids closure issues)
  const lastModifiedDateRef = useRef(null);
  // Abort in-flight /ufl/list when filters/pagination change or a newer list request starts
  const listFetchAbortControllerRef = useRef(null);
  const inFlightIsPollRef = useRef(false);

  const urlFilterValues = getUrlFilterValues(location.search);

  // --- persist pagination in sessionStorage ---
  useEffect(() => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(pagination));
  }, [pagination]);

  // Keep list filters in URL so details can forward and list can be restored.
  useSyncListPaginationToUrl({
    pagination,
    buildSearchFromPagination,
    navigate,
    location,
  });

  // --- sync ref with state to ensure they stay in sync ---
  useEffect(() => {
    lastModifiedDateRef.current = lastModifiedDate;
  }, [lastModifiedDate]);

  // Helper function to get the maximum lastModifiedDate from records
  const getMaxLastModifiedDate = (records) => {
    if (!records || records.length === 0) return null;

    const dates = records
      .map(record => record.lastModifiedDate)
      .filter(date => date != null)
      .map(date => new Date(date));

    if (dates.length === 0) return null;

    const maxDate = new Date(Math.max(...dates));
    return maxDate.toISOString();
  };

  // --- API call replacement for updateUnprocessedFaxList ---
  const fetchUnprocessedFaxList = useCallback(async (state, isPolling = false) => {
    listFetchAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    listFetchAbortControllerRef.current = abortController;
    inFlightIsPollRef.current = isPolling;

    const scheduleNextPoll = () => {
      if (document.hidden) {
        pollingActiveRef.current = false;
        return;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
      pollingTimeoutRef.current = setTimeout(() => {
        if (pollingActiveRef.current && lastModifiedDateRef.current && !document.hidden) {
          fetchUnprocessedFaxList(pagination, true);
        }
      }, nextPollDelayMs());
    };

    // Don't show loading spinner for polling requests to avoid UI flicker
    if (!isPolling) {
      setLoading(true);
    }
    setIsError(false);
    const filteredDeepCopy = filterModification(
      state.filtered,
      urlFilterValues.faxProgramCode || ""
    );
    const sortedDeepCopy = sortedModification(state.sorted);

    // Build request payload
    const requestPayload = {
      pageSize: state.pageSize,
      page: state.page,
      sorted: sortedDeepCopy,
      filtered: filteredDeepCopy,
    };

    // Add long polling parameters if this is a polling request
    // Use ref to get the latest value (avoids closure issues)
    const currentLastModifiedDate = lastModifiedDateRef.current;
    if (isPolling && currentLastModifiedDate) {
      requestPayload.lastModifiedDate = currentLastModifiedDate;
      requestPayload.pollTimeoutSeconds = POLL_TIMEOUT_SECONDS;
    }

    sendAuthenticatedAsyncRequestWithFinallyCallback(
      "/ufl/list",
      "POST",
      requestPayload,
      ({data}) => {
        const records = data.records || [];
        const lastModifiedDateForNextPoll = data.lastModifiedDateForNextPoll;

        // Update lastModifiedDate based on backend response.
        // On poll: lastModifiedDateForNextPoll === null means long-poll timed out with no change
        // to records (watermark unchanged). Non-null means the server advanced the cursor.
        if (lastModifiedDateForNextPoll != null) {
          setLastModifiedDate(lastModifiedDateForNextPoll);
          lastModifiedDateRef.current = lastModifiedDateForNextPoll;
        }

        // Poll with null lastModifiedDateForNextPoll = no change (e.g. long-poll timeout). Do not
        // apply records/totals — empty payload must not clear the grid.
        if (isPolling && lastModifiedDateForNextPoll == null) {
          scheduleNextPoll();
          return;
        }

        // Initial load, or poll where the server advanced the watermark — apply list payload
        setFaxList(records);
        setTotalPages(data.totalPages || 0);
        setTotalFaxes(data.totalElements || 0);

        // For initial load (non-polling), if backend didn't provide lastModifiedDateForNextPoll,
        // fall back to calculating from records as a backup
        if (!isPolling && !lastModifiedDateForNextPoll) {
          const maxDateFromNewRecords = getMaxLastModifiedDate(records);
          if (maxDateFromNewRecords) {
            // Use the max date from records as fallback for initial load
            setLastModifiedDate(maxDateFromNewRecords);
            lastModifiedDateRef.current = maxDateFromNewRecords;
          } else {
            // For initial load, if no records have lastModifiedDate, set to null
            // This prevents polling from starting if we can't track modifications
            setLastModifiedDate(null);
            lastModifiedDateRef.current = null;
          }
        }

        // If this was a polling request and we got updated data, immediately poll again.
        // The poll-timeout case (lastModifiedDateForNextPoll null) already scheduled the next poll above.
        if (isPolling) {
          scheduleNextPoll();
        }
      },
      (err) => {
        // On error during polling, stop polling but don't show error notification
        // to avoid spamming the user
        if (!isPolling) {
          setFaxList([]);
          setTotalFaxes(0);
          setTotalPages(0);
          setIsError(true);
          showNotification({
            title: `Error fetching fax list`,
            message:
              getErrorMessage(err.data) ||
              getErrorMessage("SOMETHING_WENT_WRONG"),
            position: "tr",
            type: "error",
          });
        } else {
          // Stop polling on error
          pollingActiveRef.current = false;
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
        }
      },
      () => {
        if (!isPolling) {
          setLoading(false);
        }
      },
      abortController.signal,
      isPolling ? UFL_LIST_POLL_HTTP_TIMEOUT_MS : null
    );
  }, [lastModifiedDate, pagination, location.search]);

  // --- setup initial filters + program details ---
  const setPrimaryDetails = useCallback(() => {
    const faxProgramCode = urlFilterValues.faxProgramCode;
    if (faxProgramCode && faxProgramCode !== UNKNOWN) {
      setLoading(true);
      sendAuthenticatedAsyncRequestWithFinallyCallback(
        `/fax-program/${faxProgramCode}`,
        "GET",
        null,
        (res) => {
          const { programName, isSwitchProgram = false } = res.data;
          const defaultFilters = buildPrimaryFilters(
            DEFAULT_UNPROCESSED_FAX_LIST_FILTER.filter(filter => !filter.faxProgramType || (filter.faxProgramType && filter.faxProgramType.includes(
                getFaxProgramType(isSwitchProgram)))),
            urlFilterValues,
            getFaxProgramType(isSwitchProgram)
          );
          setProgramName(`${programName} Fax`);
          setProgramType(getFaxProgramType(isSwitchProgram));
          setPagination((prev) => ({ ...prev, filtered: defaultFilters }));
          updateData({ ...pagination, filtered: defaultFilters });
        },
        (err) => {
          showNotification({
            title: `Unable to fetch fax program (${faxProgramCode}) details`,
            message:
              getErrorMessage(err.data) ||
              getErrorMessage("SOMETHING_WENT_WRONG"),
            position: "tr",
            type: "error",
          });
        },
        () => {
          setLoading(false);
        }
      );
    } else {
      setProgramName("Unknown Fax");
      setProgramType(UNPROCESSED_FAX_PROGRAM_TYPE.UNKNOWN);
      const defaultFilters = buildPrimaryFilters(
            DEFAULT_UNPROCESSED_FAX_LIST_FILTER.filter(filter => !filter.faxProgramType || (filter.faxProgramType && filter.faxProgramType.includes(
                UNPROCESSED_FAX_PROGRAM_TYPE.UNKNOWN))),
            urlFilterValues,
            UNPROCESSED_FAX_PROGRAM_TYPE.UNKNOWN
          );
      setPagination((prev) => ({ ...prev, filtered: defaultFilters }));
      updateData({ ...pagination, filtered: defaultFilters});
    }
  }, [location.search, pagination.filtered]);

  const getLockColumnSelection = () => {
    const lockedColumns = [];
    const isPriorityLockedFromSource = Boolean(location.state?.lockPriorityFilter);
    const associatedSwitchFilter = pagination.filtered.find(
      (f) => f.id === "associatedSwitchId"
    );
    const isAssociatedSwitchTrue =
      associatedSwitchFilter &&
      (associatedSwitchFilter.value === true || associatedSwitchFilter.value === "true");
    const isSwitchFax = programType === UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH;

    // Lock tags if from URL params
    if (urlFilterValues.faxProgramCode) {
      lockedColumns.push({ id: "tags", value: [urlFilterValues.faxProgramCode].filter(Boolean) });
    }

    // Lock priority only when navigation source explicitly requests it (priority popup flow).
    if (
      isPriorityLockedFromSource &&
      urlFilterValues.faxPriority &&
      !(isSwitchFax && isAssociatedSwitchTrue)
    ) {
      lockedColumns.push({ id: "faxPriority" });
    }

    return lockedColumns;
  }

  const updateData = useCallback(
    (state, preventUpdate = false, isPolling = false) => {
      if (!preventUpdate) {
        // Stop polling when filters/pagination change
        if (!isPolling) {
          pollingActiveRef.current = false;
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
          // Reset lastModifiedDate when filters/pagination change
          setLastModifiedDate(null);
          lastModifiedDateRef.current = null;
        }
        fetchUnprocessedFaxList(state, isPolling);
      }
    },
    [fetchUnprocessedFaxList]
  );

  // Start polling after initial data load
  const startPolling = useCallback(() => {
    // Only start polling if we have a lastModifiedDate and polling is not already active
    if (!lastModifiedDate || pollingActiveRef.current) {
      return;
    }

    pollingActiveRef.current = true;

    // Start first poll
    fetchUnprocessedFaxList(pagination, true);
  }, [lastModifiedDate, pagination, fetchUnprocessedFaxList]);

  // Stop polling when component unmounts or filters change
  useEffect(() => {
    return () => {
      pollingActiveRef.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      listFetchAbortControllerRef.current?.abort();
      listFetchAbortControllerRef.current = null;
    };
  }, []);

  // Start polling when we have a modification waterline (including empty list — new faxes can arrive)
  useEffect(() => {
    if (document.hidden) {
      return;
    }
    if (lastModifiedDate && !loading && !pollingActiveRef.current) {
      startPolling();
    }
  }, [lastModifiedDate, loading, startPolling]);

  // Background tabs should not hold a long poll. Resume with the existing watermark.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        pollingActiveRef.current = false;
        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
          pollingTimeoutRef.current = null;
        }
        if (inFlightIsPollRef.current) {
          listFetchAbortControllerRef.current?.abort();
          inFlightIsPollRef.current = false;
        }
        return;
      }

      if (lastModifiedDateRef.current && !pollingActiveRef.current) {
        pollingActiveRef.current = true;
        fetchUnprocessedFaxList(pagination, true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [pagination, fetchUnprocessedFaxList]);

  const onUpdateTable = (filterValues, sortBy, pageSize, pageNo) => {
    const associatedSwitchFilter = filterValues.find(
      (f) => f.id === "associatedSwitchId"
    );
    const previousAssociatedSwitchFilter = pagination.filtered.find(
      (f) => f.id === "associatedSwitchId"
    );
    const isAssociatedSwitchTrue =
      associatedSwitchFilter &&
      (associatedSwitchFilter.value === true || associatedSwitchFilter.value === "true");
    const wasAssociatedSwitchTrue =
      previousAssociatedSwitchFilter &&
      (previousAssociatedSwitchFilter.value === true || previousAssociatedSwitchFilter.value === "true");
    const isSwitchFax = programType === UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH;

    let processedFilterValues = [...filterValues];

    // If switching from false to true, remove only default/pre-applied tags and priority filters
    if (isSwitchFax && isAssociatedSwitchTrue && !wasAssociatedSwitchTrue) {
      // Remove only filters that are marked as default (pre-applied from URL)
      processedFilterValues = filterValues.filter(
        (f) => !(["tags", "faxPriority"].includes(f.id) && f.isdefault === true)
      );
    }
    // If switching from true to false, restore URL-based filters
    else if (isSwitchFax && !isAssociatedSwitchTrue && wasAssociatedSwitchTrue) {
      const combinedTags = getFilterValueFromUrl(
        UFL_URL_FILTER_CONFIG.find((config) => config.filterId === "tags"),
        urlFilterValues
      );
      const faxPriorityValue =
        getFilterValueFromUrl(
          UFL_URL_FILTER_CONFIG.find((config) => config.filterId === "faxPriority"),
          urlFilterValues
        );

      // Remove existing tags and priority filters first
      processedFilterValues = processedFilterValues.filter(
        (f) => !["tags", "faxPriority"].includes(f.id)
      );

      // Restore URL-based filters if they exist
      if (combinedTags.length > 0) {
        processedFilterValues.push({
          id: "tags",
          value: combinedTags,
          isdefault: true,
        });
      }

      if (faxPriorityValue) {
        processedFilterValues.push({
          id: "faxPriority",
          value: faxPriorityValue,
          isdefault: true,
        });
      }
    }

    const getTagId = (tag) =>
      typeof tag === "object" && tag !== null ? tag.id : tag;
    const currentTagsFilter = processedFilterValues.find((f) => f.id === "tags");
    if (currentTagsFilter && Array.isArray(currentTagsFilter.value)) {
      const selectedTagIds = currentTagsFilter.value.map(getTagId).filter(Boolean);
      const selectedAll = selectedTagIds.includes(ALL_FAX_TAG.id);
      const previousTagsFilter = pagination.filtered.find((f) => f.id === "tags");
      const previouslyHadAll = (previousTagsFilter?.value || [])
        .map(getTagId)
        .includes(ALL_FAX_TAG.id);

      if (selectedAll || previouslyHadAll) {
        const defaultTagIds = [urlFilterValues.faxProgramCode, ALL_FAX_TAG.id].filter(Boolean);
        const hasCustomerTagSelected = selectedTagIds.some(
          (tagId) => !defaultTagIds.includes(tagId)
        );

        if (selectedAll) {
          if (previouslyHadAll && hasCustomerTagSelected) {
            currentTagsFilter.value = currentTagsFilter.value.filter(
              (tag) => getTagId(tag) !== ALL_FAX_TAG.id
            );
          } else {
            currentTagsFilter.value = currentTagsFilter.value.filter((tag) =>
              defaultTagIds.includes(getTagId(tag))
            );
          }
        }
      }
    }

    const newState = {
      ...pagination,
      pageSize,
      page: pageNo,
      sorted: sortBy || [],
      filtered: processedFilterValues,
    };
    setPagination(newState);
    updateData(newState);
  };

  const handleToggle = useCallback(() => {
    setPagination((prevPagination) => {
      const hasShowRecentsFilter = prevPagination.filtered.some(
        (filter) => filter.id === "showMyRecentsOnly"
      );

      const updatedFiltered = hasShowRecentsFilter
        ? prevPagination.filtered.map((filter) =>
            filter.id === "showMyRecentsOnly"
              ? { ...filter, value: !filter.value }
              : filter
          )
        : [
            ...prevPagination.filtered,
            { id: "showMyRecentsOnly", value: true },
          ];

      const updatedState = { ...prevPagination, filtered: updatedFiltered };
      updateData(updatedState);
      return updatedState;
    });
  }, [updateData]);

  const isShowMyRecentsDisabled = useCallback(() => {
    const filter = pagination.filtered.find((f) => f.id === "showMyRecentsOnly");
    return filter ? filter.value : false;
  }, [pagination.filtered]);

  useEffect(() => {
    setPrimaryDetails();
    sendAuthenticatedAsyncRequest(
      "/fax/unprocessed-fax-list-filters",
      "GET",
      null,
      (res) => {
        if (res.status === 200) {
          const { recentlyViewedUsers, enabledFaxTags } = res.data.filters;
          const currentFaxTag = urlFilterValues.faxProgramCode;
          const switchAdmins = recentlyViewedUsers.map((e) => ({
            id: e.value,
            name: e.id,
          }));
          const faxTags = [...(currentFaxTag !== UNKNOWN ? [{ id: currentFaxTag, name: currentFaxTag }, { ...ALL_FAX_TAG }] : []), ...enabledFaxTags.map((e) => ({
            id: e.value,
            name: e.id,
          }))];

          switchAdmins.sort((a, b) => a.name.localeCompare(b.name));
          setSwitchAdminList(switchAdmins);
          setFaxTagList(faxTags);
        }
      },
      (res) => {
        showNotification({
          title: "Unable to fetch filters!",
          message:
            getErrorMessage(res.data) ||
            getErrorMessage("SOMETHING_WENT_WRONG"),
          position: "tr",
          type: "error",
        });
      }
    );
  }, []);

  useEffect(() => {
    if (isError) {
      showNotification({
        title: "Error Loading Data!",
        message: "You have entered invalid information.",
        position: "bl",
        type: "error",
      });
      setIsError(false);
    }
  }, [isError]);

  // Normalize ID-backed multi-select filters to option objects for UI rendering.
  useEffect(() => {
    const filterOptionsById = {
      recentlyViewedUsers: switchAdminList,
    };

    const multiSelectIdsConfigs = UFL_URL_FILTER_CONFIG.filter(
      (config) => config.valueType === QUERY_FILTER_TYPES.MULTI_SELECT_IDS
    );
    if (!multiSelectIdsConfigs.length) return;

    let hasChanged = false;
    const nextFiltered = pagination.filtered.map((filter) => {
      const config = multiSelectIdsConfigs.find((item) => item.filterId === filter.id);
      if (!config) return filter;
      const options = filterOptionsById[filter.id] || [];
      const normalizedValue = normalizeMultiSelectIdsFilterValue(filter.value, options);
      if (normalizedValue === filter.value) return filter;
      hasChanged = true;
      return { ...filter, value: normalizedValue };
    });

    if (!hasChanged) return;
    setPagination((prev) => ({ ...prev, filtered: nextFiltered }));
  }, [switchAdminList, pagination.filtered]);

  useEffect(() => {
    setListColumns(
      UNPROCESSED_FAX_LIST_COLUMNS(
        faxTagList,
        switchAdminList,
        handleToggle,
        isShowMyRecentsDisabled,
        renderLastModifiedDateCol,
        programName,
        UNPROCESSED_FAX_LIST_HIDDEN_COLUMNS[programType],
        getLockColumnSelection(),
      )
    );
  }, [programName, faxTagList, switchAdminList, programType, handleToggle, isShowMyRecentsDisabled]);

  return (
    <Container className="list-container" fluid>
      <div className="fax-program-list-panel">
        <div className="codex-version-display-bar fax-program-list-panel__header">
          <ListHeaderBackButton
            onClick={() => navigate(UFL_PROGRAM_TYPE_LIST_PATH)}
          />
          <span className="list-header-meta">
            Total Unprocessed Faxes: <strong>{totalFaxes}</strong>
          </span>
          <strong className="list-header-title">Unprocessed Fax List</strong>
          <div className="list-header-actions" aria-hidden="true">
            <Button
              size="sm"
              variant="outline-light"
              className="float-right list-header-actions-spacer"
              tabIndex={-1}
              disabled
            >
              Reset List{" "}
            </Button>
          </div>
        </div>
        <div className="fax-program-list-panel__body">
          <ServerSideReactTable
            columnDefination={listColumns}
            data={faxList}
            currentPage={pagination.page}
            sorted={pagination.sorted}
            filters={pagination.filtered}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            totalPages={totalPages}
            totalRecords={totalFaxes === "loading" ? 0 : totalFaxes}
            onUpdateTableData={onUpdateTable}
            loading={loading}
            onRowClick={(rowInfo) => {
              if (rowInfo) {
                const faxType = getFaxTypeForRoute(programType);
                const detailsPath = `/unprocessed-fax/fax-details-v2/${faxType}/${rowInfo.original.faxInfoIdentifier}${location.search || ""}`;
                navigate(detailsPath);
              }
            }}
          />
          <Tip>Tip: Hold shift when sorting to multi-sort and disable sort!</Tip>
        </div>
      </div>
    </Container>
  );
};

const UnprocessedFaxListWithDefaultQuery = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const faxProgramCode = params.get("faxProgramCode");
  if (faxProgramCode != null && faxProgramCode !== "") {
    return <UnprocessedFaxList />;
  }
  params.set("faxProgramCode", UNKNOWN);
  return (
    <Navigate
      to={`${location.pathname}?${params.toString()}`}
      replace
    />
  );
};

export default UnprocessedFaxListWithDefaultQuery;
